import type { ChecklistFinding, MilestoneDocument } from '@/types';

interface Props {
    document: MilestoneDocument;
}

const FINDING_BADGE: Record<ChecklistFinding, string> = {
    yes: 'bg-green-50 text-green-700 border-green-200',
    no: 'bg-red-50 text-red-700 border-red-200',
    n_a: 'bg-gray-50 text-gray-600 border-gray-200',
};

const FINDING_LABEL: Record<ChecklistFinding, string> = {
    yes: 'Yes',
    no: 'No',
    n_a: 'N/A',
};

function formatSubmittedAt(value: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

export default function MilestoneDocumentView({ document }: Props) {
    if (!document.blocks || document.blocks.length === 0) {
        return null;
    }

    return (
        <section className="flex flex-col gap-6">
            <header className="border-b border-[#e0e0e0] pb-4">
                <h2 className="text-lg font-bold text-[#1b1b1b]">Investigation milestones</h2>
                <p className="text-xs text-[#4e4d4d] mt-1">
                    {document.submittedBlocks} of {document.totalBlocks} sections submitted by the field agent. Findings
                    and supporting notes are listed verbatim below.
                </p>
            </header>

            <div className="flex flex-col gap-6">
                {document.blocks.map((block) => (
                    <article
                        key={block.sectionId}
                        className="bg-white border border-[#e0e0e0] rounded-xl overflow-hidden"
                    >
                        <div className="px-5 py-4 border-b border-[#f0f0f0] bg-[#fafafa]">
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                                <h3 className="text-base font-bold text-[#1b1b1b]">
                                    {block.order}. {block.title}
                                </h3>
                                {block.submittedAt && (
                                    <p className="text-xs text-[#4e4d4d]">
                                        Submitted {formatSubmittedAt(block.submittedAt)}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="px-5 py-4 flex flex-col gap-5">
                            {block.summary && (
                                <div>
                                    <p className="text-[11px] uppercase tracking-wider font-semibold text-[#717680] mb-1">
                                        Summary
                                    </p>
                                    <p className="text-sm text-[#1b1b1b] leading-relaxed">{block.summary}</p>
                                </div>
                            )}

                            {block.details && (
                                <div>
                                    <p className="text-[11px] uppercase tracking-wider font-semibold text-[#717680] mb-1">
                                        Details
                                    </p>
                                    <p className="text-sm text-[#1b1b1b] whitespace-pre-wrap leading-relaxed">
                                        {block.details}
                                    </p>
                                </div>
                            )}

                            {block.findings.length > 0 && (
                                <div>
                                    <p className="text-[11px] uppercase tracking-wider font-semibold text-[#717680] mb-2">
                                        Findings
                                    </p>
                                    <ul className="flex flex-col gap-3">
                                        {block.findings.map((f) => (
                                            <li
                                                key={f.itemId}
                                                className="border border-[#f0f0f0] rounded-lg px-3 py-2.5"
                                            >
                                                <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                                                    <p className="text-sm text-[#1b1b1b] leading-snug">{f.question}</p>
                                                    {f.finding && (
                                                        <span
                                                            className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${FINDING_BADGE[f.finding]}`}
                                                        >
                                                            {FINDING_LABEL[f.finding]}
                                                        </span>
                                                    )}
                                                </div>
                                                {f.notes && (
                                                    <p className="text-xs text-[#4e4d4d] whitespace-pre-wrap leading-relaxed">
                                                        {f.notes}
                                                    </p>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
}
